import { useState } from "react";
import { Check, ChevronDown, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const INITIALS = (m) => (m?.name?.[0] || m?.email?.[0] || "?").toUpperCase();

const COLORS = ["bg-[#002FA7]", "bg-emerald-700", "bg-amber-600", "bg-rose-600", "bg-purple-700", "bg-sky-700", "bg-gray-800"];
const colorFor = (id) => {
  if (!id) return COLORS[0];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
};

/**
 * AssigneeStack — overlapping avatar pile used on task cards.
 *  assigneeIds: array of user_id strings
 *  members:     array of {user_id, name, email, picture}
 *  size:        Tailwind size class (e.g. "w-6 h-6")
 */
export const AssigneeStack = ({ assigneeIds = [], members = [], size = "w-6 h-6", max = 3, testId }) => {
  const valid = (assigneeIds || []).filter(Boolean);
  if (valid.length === 0) return null;
  const visible = valid.slice(0, max);
  const overflow = valid.length - visible.length;
  return (
    <div className="flex -space-x-1.5" data-testid={testId}>
      {visible.map((uid) => {
        const m = members.find((x) => x.user_id === uid);
        return (
          <div
            key={uid}
            className={`${size} rounded-full ring-2 ring-white text-white text-[10px] grid place-items-center font-semibold overflow-hidden ${colorFor(uid)}`}
            title={m?.name || m?.email || uid}
          >
            {m?.picture ? (
              <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
            ) : (
              INITIALS(m || { name: uid })
            )}
          </div>
        );
      })}
      {overflow > 0 && (
        <div className={`${size} rounded-full ring-2 ring-white bg-gray-700 text-white text-[10px] grid place-items-center font-semibold`}>
          +{overflow}
        </div>
      )}
    </div>
  );
};

/**
 * AssigneePicker — multi-select dropdown for choosing workspace members.
 *  value:    array of selected user_ids
 *  members:  array of workspace members
 *  onChange: (newIds: string[]) => void
 */
export const AssigneePicker = ({ value = [], members = [], onChange, testId = "assignee-picker" }) => {
  const [open, setOpen] = useState(false);
  const selected = value || [];

  const toggle = (uid) => {
    onChange(selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 border border-[#374151] bg-[#030712] mt-1 px-3 text-sm flex items-center justify-between gap-2 hover:bg-[#111827] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          data-testid={`${testId}-trigger`}
        >
          <span className="flex items-center gap-2 truncate text-left">
            {selected.length === 0 ? (
              <>
                <Users className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.7} />
                <span className="text-gray-500">Choose people…</span>
              </>
            ) : (
              <>
                <AssigneeStack assigneeIds={selected} members={members} size="w-5 h-5" max={4} />
                <span className="text-white text-xs truncate">
                  {selected.length === 1
                    ? (members.find((m) => m.user_id === selected[0])?.name || "1 assigned")
                    : `${selected.length} assigned`}
                </span>
              </>
            )}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={1.7} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="rounded-none border-gray-900 p-0 w-72" align="start">
        <div className="px-3 py-2 border-b border-[#1F2937] text-[10px] uppercase tracking-[0.2em] text-gray-500">
          Workspace members ({members.length})
        </div>
        <div className="max-h-64 overflow-y-auto">
          {members.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-500">Invite teammates from the Team page to assign them tasks.</div>
          )}
          {members.map((m) => {
            const checked = selected.includes(m.user_id);
            return (
              <button
                key={m.user_id}
                type="button"
                onClick={() => toggle(m.user_id)}
                className={`w-full px-3 py-2 flex items-center gap-3 text-left text-sm hover:bg-gray-50 ${checked ? "bg-blue-50/50" : ""}`}
                data-testid={`${testId}-option-${m.user_id}`}
              >
                <div className={`w-6 h-6 rounded-full text-white text-[10px] grid place-items-center font-semibold overflow-hidden flex-shrink-0 ${colorFor(m.user_id)}`}>
                  {m.picture ? <img src={m.picture} alt={m.name} className="w-full h-full object-cover" /> : INITIALS(m)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{m.name}</div>
                  <div className="truncate text-[11px] text-gray-500">{m.email}</div>
                </div>
                <div className={`w-4 h-4 border ${checked ? "bg-[#002FA7] border-[#002FA7]" : "border-[#374151]"} grid place-items-center flex-shrink-0`}>
                  {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-[#1F2937] px-3 py-2 flex justify-between items-center">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] uppercase tracking-[0.2em] text-gray-500 hover:text-gray-900"
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#002FA7] hover:text-[#002480]"
              data-testid={`${testId}-done`}
            >
              Done
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
